// How each exam is actually structured: its papers (sections) in sitting order, with the official names, time
// allowed and number of questions where published. Drives the section names on the subject page, the timed
// "Section test" (one paper at real length and time) and the Full Mock Exam (every paper in order).
// Sources and anything unverified are listed with each exam in EXAM_STRUCTURE_SOURCES below.
//
// paper: { subject: <app subject id>, name, minutes: number | { y3: n, ... }, questions?: number | { y3: n, ... },
//          writing?: true (a written task, run by the timed writing test) }

// showQuestions: the official question count is published (shown to students); otherwise `questions` is only how
// long our version of the paper is. mock: false where the papers are separate tests on different days (ICAS).
const NAPLAN_LEN = { y3: 36, y5: 36, y7: 42, y9: 42 }; // NAPLAN is adaptive and publishes no fixed count
const ACER_PRIMARY = ['y3', 'y4', 'y5'];               // sitting in Years 3-5 for entry to Years 4-6 (Level P)
const acer = (primary, secondary) => ({ y3: primary, y4: primary, y5: primary, y6: secondary, y7: secondary });

export const EXAM_STRUCTURE = {
  naplan: {
    years: ['y3', 'y5', 'y7', 'y9'],
    papers: [
      { subject: 'writing', name: 'Writing', writing: true, minutes: { y3: 40, y5: 42, y7: 42, y9: 42 } },
      { subject: 'reading', name: 'Reading', minutes: { y3: 45, y5: 50, y7: 65, y9: 65 }, questions: NAPLAN_LEN },
      { subject: 'language', name: 'Conventions of language', minutes: 45, questions: 36 },
      { subject: 'numeracy', name: 'Numeracy', minutes: { y3: 45, y5: 50, y7: 65, y9: 65 }, questions: NAPLAN_LEN },
    ],
  },
  selective_nsw: {
    years: ['y6'],
    papers: [
      { subject: 'reading', name: 'Reading', minutes: 45, questions: 38 }, // 17 questions, 38 answers in all
      { subject: 'numeracy', name: 'Mathematical Reasoning', minutes: 40, questions: 35, showQuestions: true },
      { subject: 'thinking', name: 'Thinking Skills', minutes: 40, questions: 40, showQuestions: true },
      { subject: 'writing', name: 'Writing', writing: true, minutes: 30 },
    ],
  },
  oc: {
    years: ['y4'],
    papers: [
      { subject: 'reading', name: 'Reading', minutes: 40, questions: 33 }, // 14 questions, 33 answers in all
      { subject: 'numeracy', name: 'Mathematical Reasoning', minutes: 40, questions: 35, showQuestions: true },
      { subject: 'thinking', name: 'Thinking Skills', minutes: 30, questions: 30, showQuestions: true },
    ],
  },
  icas: {
    mock: false,
    papers: [
      { subject: 'english', name: 'English', showQuestions: true,
        minutes: { y3: 45, y4: 45, y5: 50, y6: 50, y7: 55, y8: 55, y9: 60, y10: 60 },
        questions: { y3: 45, y4: 45, y5: 50, y6: 50, y7: 50, y8: 50, y9: 55, y10: 55 } },
      { subject: 'numeracy', name: 'Mathematics', showQuestions: true,
        minutes: { y3: 45, y4: 45, y5: 45, y6: 60, y7: 60, y8: 60, y9: 60, y10: 60 },
        questions: { y3: 40, y4: 40, y5: 40, y6: 40, y7: 40, y8: 40, y9: 40, y10: 40 } },
      { subject: 'science', name: 'Science', showQuestions: true,
        minutes: { y3: 45, y4: 45, y5: 55, y6: 55, y7: 60, y8: 60, y9: 60, y10: 60 },
        questions: { y3: 30, y4: 30, y5: 40, y6: 40, y7: 40, y8: 40, y9: 40, y10: 40 } },
      { subject: 'digital', name: 'Digital Technologies', showQuestions: true,
        minutes: { y3: 30, y4: 30, y5: 35, y6: 35, y7: 40 }, questions: { y3: 30, y4: 30, y5: 35, y6: 35, y7: 40 } },
      { subject: 'spelling', name: 'Spelling Bee', showQuestions: true,
        minutes: { y3: 40, y4: 40, y5: 40, y6: 40, y7: 40 }, questions: { y3: 40, y4: 45, y5: 45, y6: 50, y7: 50 } },
      { subject: 'writing', name: 'Writing', writing: true, minutes: 35 },
    ],
  },
  acer: {
    mock: false, // the order the papers are sat in is not published
    papers: [
      { subject: 'reading', name: acer('Reading and Viewing', 'Humanities'), minutes: acer(30, 40), questions: acer(25, 40) },
      { subject: 'numeracy', name: 'Mathematics', minutes: acer(30, 40), questions: acer(20, 32) },
      { subject: 'writing', name: acer('Writing', 'Written Expression'), writing: true, minutes: acer(40, 50) },
    ],
  },
  ignite: {
    mock: false, // question counts are not published
    papers: [
      { subject: 'reading', name: 'Reading Comprehension', minutes: 45 },
      { subject: 'numeracy', name: 'Mathematical Reasoning', minutes: 40 },
      { subject: 'thinking', name: 'Abstract Reasoning', minutes: 30 },
      { subject: 'writing', name: 'Written Expression', writing: true, minutes: 25 },
    ],
  },
  vic_selective: {
    mock: false, // question counts are not published
    papers: [
      { subject: 'numeracy', name: 'Mathematics and Quantitative Reasoning', minutes: 60 },
      { subject: 'reading', name: 'Reading and Verbal Reasoning', minutes: 55 },
      { subject: 'writing', name: 'Writing', writing: true, minutes: 40 },
    ],
    extraNames: { thinking: 'Reasoning practice' },
  },
};

export const EXAM_STRUCTURE_SOURCES = {
  naplan: ['https://www.nap.edu.au/online-assessment/test-window', 'https://www.nap.edu.au/naplan/whats-in-the-tests'],
  selective_nsw: ['https://education.nsw.gov.au/schooling/parents-and-carers/choosing-a-school-setting/selective-high-schools/placement-test'],
  oc: ['https://education.nsw.gov.au/schooling/parents-and-carers/choosing-a-school-setting/selective-high-schools/placement-test'],
  icas: ['https://www.icasassessments.com/icas-year-levels-and-exam-papers/'],
  acer: ['https://www.acer.org/au/scholarships', 'https://www.acer.org/flyin-render-generic/default_site/scholarship_2025/flyin-primary-level', 'https://www.acer.org/flyin-render-generic/default_site/scholarship_2025/flyin-secondary-level'],
  ignite: ['https://gihs.sa.edu.au/specialist-programs/ignite', 'https://www.theheights.sa.edu.au/wp-content/uploads/2025/12/IGNITE-prospective-families-2025.pdf'],
  vic_selective: ['https://selectiveentry.acer.org/vic', 'https://selectiveentry.acer.org/vic/exam-day'],
};
export { ACER_PRIMARY };

const perYear = (v, yearId) => (v && typeof v === 'object' ? v[yearId] : v);

/** The papers of an exam for a year level, in sitting order, with minutes/questions resolved for that year. */
export function getPapers(program, yearId) {
  const ex = EXAM_STRUCTURE[program];
  if (!ex || (ex.years && !ex.years.includes(yearId))) return [];
  return ex.papers
    .map((p) => ({ ...p, name: perYear(p.name, yearId), minutes: perYear(p.minutes, yearId), questions: perYear(p.questions, yearId) }))
    .filter((p) => p.minutes);
}

/** One paper (by app subject id) for a section test, or null when its official length is not known. */
export function getPaper(program, yearId, subject) {
  const p = getPapers(program, yearId).find((x) => x.subject === subject);
  return p && (p.writing || p.questions) ? p : null;
}

/** Whether this exam is sat as one sitting of several papers, so a full mock exam makes sense. */
export const hasMock = (program) => !!EXAM_STRUCTURE[program] && EXAM_STRUCTURE[program].mock !== false;

/** The official section name for an app subject in this exam ("Conventions of language"), else the app's label. */
export function getSectionName(program, subject, fallback, yearId) {
  const ex = EXAM_STRUCTURE[program];
  const p = ex && ex.papers.find((x) => x.subject === subject);
  return (p && (perYear(p.name, yearId) || perYear(p.name, 'y6'))) || (ex && ex.extraNames && ex.extraNames[subject]) || fallback;
}
